// ─── Shared navigation data model ────────────────────────────────────
//
// The single source of truth for the app's module navigation: which modules
// live in which section, their icons, i18n labels, target URLs, permission
// gates, and sub-pages. Consumed by BOTH the desktop sidebar (NavbarV2) and
// the mobile top-bar/drawer so the two surfaces can never drift apart — the
// failure mode of the previous setup, which redefined this data in each
// component.
//
// In the app these are driven by the router + per-tenant permissions; the
// prototype's hardcoded CTX.system / CTX.admin seed data is replaced by this.
// ─────────────────────────────────────────────────────────────────────

import measureSvg from '../../assets/icons/measure.svg'
import productionSvg from '../../assets/icons/production.svg'
import contactSvg from '../../assets/icons/contact.svg'
import offerSvg from '../../assets/icons/offer.svg'
import warehouseSvg from '../../assets/icons/warehouse.svg'
import agreementSvg from '../../assets/icons/agreement.svg'
import transportSvg from '../../assets/icons/transport.svg'
import installationSvg from '../../assets/icons/installation.svg'
import serviceSvg from '../../assets/icons/service.svg'
import impressionSvg from '../../assets/icons/impression.svg'
import analyticsSvg from '../../assets/icons/analytics.svg'

export interface NavSection {
    id: string
    labelId: string
    defaultLabel: string
    modules: string[]
}

// Section grouping for the Sistemi (Sales) context. 'analytics' renders the
// canonical light-theme Analitika page and is admin-only (see canSeeModule).
export const SECTIONS: NavSection[] = [
    { id: 'insights',  labelId: 'nav.section.insights',  defaultLabel: 'Insights',   modules: ['analytics'] },
    { id: 'sales',     labelId: 'nav.section.sales',     defaultLabel: 'Sales',      modules: ['contact', 'measures', 'offers', 'agreements'] },
    { id: 'ops',       labelId: 'nav.section.ops',       defaultLabel: 'Operations', modules: ['production', 'warehouse', 'transport', 'installation'] },
    { id: 'aftercare', labelId: 'nav.section.aftercare', defaultLabel: 'After-care', modules: ['service', 'impression'] },
]

export const MODULE_I18N_KEY: Record<string, string> = {
    analytics: 'nav.module.analytics',
    contact: 'nav.module.contact',
    measures: 'nav.module.measures',
    offers: 'nav.module.offers',
    agreements: 'nav.module.agreements',
    production: 'nav.module.production',
    warehouse: 'nav.module.warehouse',
    transport: 'nav.module.transport',
    installation: 'nav.module.installation',
    service: 'nav.module.service',
    impression: 'nav.module.impression',
}

export const MODULE_ICON: Record<string, string> = {
    analytics: analyticsSvg,
    contact: contactSvg,
    measures: measureSvg,
    offers: offerSvg,
    agreements: agreementSvg,
    production: productionSvg,
    warehouse: warehouseSvg,
    transport: transportSvg,
    installation: installationSvg,
    service: serviceSvg,
    impression: impressionSvg,
}

// Most modules route to `/dashboard/{key}`; override when key ≠ URL segment.
export const MODULE_URL_OVERRIDE: Record<string, string> = {
    // Production lands on the board, not the legacy list page.
    production: '/dashboard/production/board',
}

export const moduleUrl = (moduleKey: string): string =>
    MODULE_URL_OVERRIDE[moduleKey] || `/dashboard/${moduleKey}`

export interface SubItem {
    labelKey: string
    defaultLabel: string
    path: string
    /** Optional permission gate evaluated against the permissions object. */
    canSee?: (perms: any) => boolean
}

// Sub-pages per module. The mobile drawer renders these inline under an
// expandable module row; the desktop sidebar renders its own submenu under the
// active module. Both reach the same routes wired in Dashboard.tsx / module
// routers.
export const MODULE_SUB_ITEMS: Record<string, SubItem[]> = {
    offers: [
        { labelKey: 'nav.sub.all',              defaultLabel: 'View all',          path: '/dashboard/offers',                  canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.canceledProducts', defaultLabel: 'Canceled products', path: '/dashboard/offers/canceled/products' },
    ],
    agreements: [
        { labelKey: 'nav.sub.agreements',        defaultLabel: 'Agreements', path: '/dashboard/agreements',                  canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.agreementCalendar', defaultLabel: 'Calendar',   path: '/dashboard/agreements/project/calendar' },
    ],
    production: [
        { labelKey: 'nav.sub.productionInformation', defaultLabel: 'Production info',    path: '/dashboard/production/board',                     canSee: (p) => !!(p?.fullPrivileges || p?.production) },
        { labelKey: 'nav.sub.productionProducts',    defaultLabel: 'Ordered products',   path: '/dashboard/production/products',                  canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.productionCalendar',    defaultLabel: 'Calendar',           path: '/dashboard/production/calendar',                  canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.archivedProcesses',     defaultLabel: 'Process archive',    path: '/dashboard/production/archived-responsibilities', canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.productionArchive',     defaultLabel: 'Production archive', path: '/dashboard/production/archive',                   canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.archivedProducts',      defaultLabel: 'Product archive',    path: '/dashboard/production/archived-products',         canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.returnedProcesses',     defaultLabel: 'Return history',     path: '/dashboard/production/returned-processes',        canSee: (p) => !!p?.fullPrivileges },
        { labelKey: 'nav.sub.passiveProducts',       defaultLabel: 'Passive products',   path: '/dashboard/production/passive-products',          canSee: (p) => !!p?.fullPrivileges },
    ],
    impression: [
        { labelKey: 'nav.sub.impressionPending', defaultLabel: 'Pending projects', path: '/dashboard/impression/1' },
        { labelKey: 'nav.sub.impressionAll',     defaultLabel: 'All impressions',  path: '/dashboard/impression/2' },
        { labelKey: 'nav.sub.impressionCreate',  defaultLabel: 'Create new',       path: '/dashboard/impression/create' },
    ],
}

export const isModuleEnabled = (moduleKey: string, companyProfile: any): boolean => {
    const enabled = companyProfile?.enabledModules
    if (enabled == null) return true
    return Array.isArray(enabled) && enabled.includes(moduleKey)
}

export const canSeeModule = (moduleKey: string, permissions: any, companyProfile: any): boolean => {
    const p = permissions || {}
    // Analytics is admin-only (fullPrivileges OR superAdmin — never a per-module
    // grant) AND respects the per-company enabled_modules toggle from Mika Ops.
    if (moduleKey === 'analytics') {
        return !!((p.fullPrivileges || p.superAdmin) && isModuleEnabled(moduleKey, companyProfile))
    }
    return !!((p.fullPrivileges || p[moduleKey]) && isModuleEnabled(moduleKey, companyProfile))
}
